import { useState } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
  User, Calendar, FileSpreadsheet, Eye, Star, Share2, TrendingUp,
  Users, Building2, BarChart3, Lightbulb, BookOpen, LayoutGrid, Trophy,
  ChevronRight, Headphones, Send, CheckCircle2, Clock, AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { INSTITUTION_TREE } from '@/data/shared-templates'
import {
  USER_PROFILE,
  USER_ACTIVITY,
  USER_MONTHLY_ACTIVITY,
  USER_ROLE_RANKING,
  USER_DEPT_RANKING,
  USER_BANK_RANKING,
  SUGGESTED_ACTIONS,
  RECOMMENDED_CONTENT,
  SERVICE_TICKETS,
  type ServiceTicket,
} from '@/data/user-center-data'

/** 从机构树收集所有节点名称及其完整路径 */
function collectInstitutions(nodes: typeof INSTITUTION_TREE, path: string = ''): Map<string, string> {
  const map = new Map<string, string>()
  for (const node of nodes) {
    const fullPath = path ? `${path} / ${node.name}` : node.name
    map.set(node.name, fullPath)
    if (node.children) collectInstitutions(node.children, fullPath)
  }
  return map
}

/** 常见机构简称 → 全称映射 */
const INSTITUTION_ABBR: Record<string, string> = {
  '海珠中支': '海珠中心支行',
  '广州分行': '广州分行',
  '佛山分行': '佛山分行',
  '东莞分行': '东莞分行',
  '惠州分行': '惠州分行',
}

/** 根据用户名解析姓名、职位、机构 */
function parseUserInfo(username: string): { name: string; role: string; department: string } {
  const institutions = collectInstitutions(INSTITUTION_TREE)

  // 构建匹配列表：全称 + 简称（优先长名称）
  const matchList: Array<{ keyword: string; fullName: string }> = []
  for (const [abbr, full] of Object.entries(INSTITUTION_ABBR)) {
    matchList.push({ keyword: abbr, fullName: full })
  }
  for (const [name, full] of institutions) {
    matchList.push({ keyword: name, fullName: full })
  }
  matchList.sort((a, b) => b.keyword.length - a.keyword.length)

  let matchedFullName: string | null = null
  let matchedKeyword: string | null = null

  for (const { keyword, fullName } of matchList) {
    if (username.includes(keyword)) {
      matchedKeyword = keyword
      matchedFullName = fullName
      break
    }
  }

  if (!matchedKeyword || !matchedFullName) {
    // 特殊角色：金融科技部运营
    if (username === '金融科技部运营') {
      return { name: username, role: '数据运营', department: '金融科技部（数字化转型办公室）' }
    }
    return { name: username, role: USER_PROFILE.role, department: USER_PROFILE.department }
  }

  // 判断是否为支行（叶子节点，无子节点）→ 网点行长
  const isLeaf = (() => {
    const walk = (nodes: typeof INSTITUTION_TREE): boolean => {
      for (const n of nodes) {
        if (n.name === matchedFullName) return !n.children || n.children.length === 0
        if (n.children && walk(n.children)) return true
      }
      return false
    }
    return walk(INSTITUTION_TREE)
  })()

  // 从用户名中提取职位：去掉匹配关键字后的部分
  const idx = username.indexOf(matchedKeyword)
  const suffix = username.substring(idx + matchedKeyword.length)

  let role: string
  if (isLeaf) {
    role = '网点行长'
  } else if (suffix === '行长') {
    role = '行长'
  } else if (suffix) {
    role = suffix
  } else {
    role = USER_PROFILE.role
  }

  return { name: username, role, department: matchedFullName }
}

interface UserCenterPageProps {
  onViewReport?: (templateId?: string) => void
  onLearnMore?: (catalogId: string) => void
  onCaseDetail?: (caseId: string) => void
  onPayrollTopicAnalysis?: () => void
  version?: string
  currentUser?: string
}

export function UserCenterPage({ onViewReport, onLearnMore, onCaseDetail, onPayrollTopicAnalysis, version, currentUser }: UserCenterPageProps) {
  const userInfo = currentUser ? parseUserInfo(currentUser) : { name: USER_PROFILE.name, role: USER_PROFILE.role, department: USER_PROFILE.department }
  const [tickets, setTickets] = useState<ServiceTicket[]>(SERVICE_TICKETS)
  const [showServiceDialog, setShowServiceDialog] = useState(false)
  const [serviceQuestion, setServiceQuestion] = useState('')
  const handleTopicClick = (topic: { id: string; catalogId?: string }) => {
    if (topic.id === 'topic-payroll') {
      onPayrollTopicAnalysis?.()
    } else if (topic.catalogId) {
      onLearnMore?.(topic.catalogId)
    }
  }

  const handleTemplateClick = (templateId: string) => {
    onViewReport?.(templateId)
  }

  const handleCaseClick = (caseId: string) => {
    onCaseDetail?.(caseId)
  }

  const handleSubmitTicket = () => {
    if (!serviceQuestion.trim()) return
    const now = new Date()
    const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
    const newTicket: ServiceTicket = {
      id: `ticket-${Date.now()}`,
      question: serviceQuestion.trim(),
      status: 'pending',
      createdAt: timeStr,
    }
    setTickets(prev => [newTicket, ...prev])
    setServiceQuestion('')
    setShowServiceDialog(false)

    // 模拟服务中心回复
    setTimeout(() => {
      setTickets(prev =>
        prev.map(t =>
          t.id === newTicket.id
            ? { ...t, status: 'processing' as const }
            : t
        )
      )
    }, 2000)
    setTimeout(() => {
      const replyTime = new Date()
      const replyTimeStr = `${replyTime.getFullYear()}-${String(replyTime.getMonth() + 1).padStart(2, '0')}-${String(replyTime.getDate()).padStart(2, '0')} ${String(replyTime.getHours()).padStart(2, '0')}:${String(replyTime.getMinutes()).padStart(2, '0')}`
      setTickets(prev =>
        prev.map(t =>
          t.id === newTicket.id
            ? {
                ...t,
                status: 'resolved' as const,
                reply: `您好！关于您反馈的"${serviceQuestion.slice(0, 20)}..."问题，我们已收到并正在处理中。如有进一步进展，将第一时间通知您。感谢您的反馈！`,
                repliedAt: replyTimeStr,
              }
            : t
        )
      )
    }, 5000)
  }

  return (
    <>
    <ScrollArea className="flex-1">
      <div className="p-6 bg-gray-50 min-h-full">
        {/* 用户信息卡片 */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-20 relative">
            <div className="absolute -bottom-8 left-6 flex items-end gap-4">
              <div className="w-16 h-16 rounded-full bg-white border-4 border-white shadow-md flex items-center justify-center">
                <User className="w-8 h-8 text-gcb-primary" />
              </div>
              <div className="pb-2">
                <h1 className="text-lg font-bold text-gray-900">{userInfo.name}</h1>
                <p className="text-xs text-gray-500">{userInfo.role} · {userInfo.department}</p>
              </div>
            </div>
          </div>
          <CardContent className="pt-12 pb-4">
            <div className="grid grid-cols-6 gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-gcb-primary">{USER_ACTIVITY.totalLoginDays}</p>
                <p className="text-xs text-gray-500 mt-0.5">累计登录天数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-gcb-primary">{USER_ACTIVITY.thisMonthLoginDays}</p>
                <p className="text-xs text-gray-500 mt-0.5">本月登录天数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-600">{USER_ACTIVITY.totalReportsCreated}</p>
                <p className="text-xs text-gray-500 mt-0.5">制作报表数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-purple-600">{USER_ACTIVITY.totalReportsViewed.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-0.5">查看报表次数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-amber-600">{USER_ACTIVITY.totalFavorites}</p>
                <p className="text-xs text-gray-500 mt-0.5">收藏数</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-blue-600">{USER_ACTIVITY.consecutiveLoginDays}天</p>
                <p className="text-xs text-gray-500 mt-0.5">连续登录</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 排名对比 */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* 同岗位用户排名 */}
          <RankingCard
            title="同岗位用户"
            icon={<Users className="w-4 h-4" />}
            iconColor="text-blue-600"
            iconBg="bg-blue-100"
            ranking={USER_ROLE_RANKING}
          />
          {/* 本机构用户排名 */}
          <RankingCard
            title="本机构用户"
            icon={<Building2 className="w-4 h-4" />}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-100"
            ranking={USER_DEPT_RANKING}
          />
          {/* 全行用户排名 */}
          <RankingCard
            title="全行用户"
            icon={<BarChart3 className="w-4 h-4" />}
            iconColor="text-purple-600"
            iconBg="bg-purple-100"
            ranking={USER_BANK_RANKING}
          />
        </div>

        {/* 月度活跃趋势 */}
        <Card className="mb-6">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-gcb-primary" />
              <h3 className="text-sm font-semibold text-gray-900">近6个月活跃趋势</h3>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {(() => {
              const maxVal = Math.max(...USER_MONTHLY_ACTIVITY.map(x => x.reportsViewed))
              const chartH = 100
              const points = USER_MONTHLY_ACTIVITY.map((m, i) => ({
                x: ((i + 0.5) / USER_MONTHLY_ACTIVITY.length) * 100,
                y: chartH - (m.reportsViewed / maxVal) * chartH,
              }))
              const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
              return (
                <div className="relative h-32">
                  <svg viewBox={`0 0 100 ${chartH}`} className="w-full h-full" preserveAspectRatio="none">
                    {/* 折线 */}
                    <path d={linePath} fill="none" stroke="#6366f1" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
                    {/* 面积 */}
                    <path d={`${linePath} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`} fill="#6366f1" opacity="0.08" />
                    {/* 数据点 */}
                    {points.map((p, i) => (
                      <circle key={i} cx={p.x} cy={p.y} r="1.5" fill="#6366f1" stroke="#fff" strokeWidth="0.5" />
                    ))}
                  </svg>
                  {/* 数值标签 */}
                  <div className="absolute inset-0 flex">
                    {USER_MONTHLY_ACTIVITY.map((m, i) => (
                      <div key={i} className="flex-1 flex justify-center">
                        <span className="text-[10px] text-gray-400 -mt-4">{m.reportsViewed}</span>
                      </div>
                    ))}
                  </div>
                  {/* 横坐标年月 */}
                  <div className="absolute bottom-0 inset-x-0 flex">
                    {USER_MONTHLY_ACTIVITY.map((m, i) => (
                      <div key={i} className="flex-1 flex justify-center">
                        <span className="text-[10px] text-gray-500">{m.month}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
            <p className="text-[10px] text-gray-400 mt-3 leading-relaxed">
              根据您在本平台的登录、访问、制作报表、使用时长等操作综合测算活跃度分值。
            </p>
          </CardContent>
        </Card>

        {/* 建议操作 - v2.0 only */}
        {version !== '1' && (
          <Card className="mb-6">
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-semibold text-gray-900">下一步建议</h3>
                <span className="text-xs text-gray-400 ml-auto">基于您的使用习惯推荐</span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-50">
                {SUGGESTED_ACTIONS.map((action) => (
                  <div key={action.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="text-xl shrink-0">{action.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-gray-900">{action.title}</h4>
                          <Badge
                            variant={action.priority === 'high' ? 'default' : 'outline'}
                            className={cn(
                              'text-[10px] h-4 px-1.5',
                              action.priority === 'high' ? 'bg-red-500 hover:bg-red-600' : 'border-gray-200 text-gray-500'
                            )}
                          >
                            {action.priority === 'high' ? '优先' : '推荐'}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{action.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 推荐内容 - v2.0 only */}
        {version !== '1' && (
        <div className="grid grid-cols-3 gap-4">
          {/* 推荐模板 */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-3 border-b bg-gradient-to-r from-purple-50 to-white">
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-semibold text-gray-900">推荐模板</h3>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-50">
                {RECOMMENDED_CONTENT.templates.map((t) => (
                  <div
                    key={t.id}
                    className="p-3 hover:bg-purple-50/50 cursor-pointer transition-colors group"
                    onClick={() => handleTemplateClick(t.id)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center shrink-0">
                        <FileSpreadsheet className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{t.name}</h4>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t.department}</p>
                        <span className="text-[10px] text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded mt-1 inline-block">{t.reason}</span>
                      </div>
                      <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-gcb-primary transition-colors shrink-0 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 推荐主题 */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-3 border-b bg-gradient-to-r from-emerald-50 to-white">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-900">推荐主题</h3>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-50">
                {RECOMMENDED_CONTENT.topics.map((t) => (
                  <div
                    key={t.id}
                    className="p-3 hover:bg-emerald-50/50 cursor-pointer transition-colors group"
                    onClick={() => handleTopicClick(t)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded bg-emerald-100 flex items-center justify-center shrink-0">
                        <BarChart3 className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{t.name}</h4>
                        <p className="text-[10px] text-gray-400 mt-0.5">{t.department}</p>
                        <span className="text-[10px] text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded mt-1 inline-block">{t.reason}</span>
                      </div>
                      <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-gcb-primary transition-colors shrink-0 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 推荐案例 */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-3 border-b bg-gradient-to-r from-amber-50 to-white">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-600" />
                <h3 className="text-sm font-semibold text-gray-900">推荐案例</h3>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-50">
                {RECOMMENDED_CONTENT.cases.map((c) => (
                  <div
                    key={c.id}
                    className="p-3 hover:bg-amber-50/50 cursor-pointer transition-colors group"
                    onClick={() => handleCaseClick(c.id)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded bg-amber-100 flex items-center justify-center shrink-0 text-sm">
                        📊
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-medium text-gray-900 truncate group-hover:text-gcb-primary transition-colors">{c.name}</h4>
                        <p className="text-[10px] text-gray-400 mt-0.5">{c.department}</p>
                        <span className="text-[10px] text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded mt-1 inline-block">{c.reason}</span>
                      </div>
                      <ChevronRight className="w-3 h-3 text-gray-300 group-hover:text-gcb-primary transition-colors shrink-0 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* 一键服务 */}
        <Card className="mb-6">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Headphones className="w-4 h-4 text-gcb-primary" />
                <h3 className="text-sm font-semibold text-gray-900">一键服务</h3>
              </div>
              <Button size="sm" onClick={() => setShowServiceDialog(true)} className="bg-gcb-primary hover:bg-gcb-primaryDark text-white">
                <Send className="w-3.5 h-3.5 mr-1" />
                提交问题
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {tickets.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-xs">
                暂无服务记录
              </div>
            ) : (
              <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
                {tickets.map((ticket) => (
                  <div key={ticket.id} className="p-4 hover:bg-gray-50 transition-colors">
                    {/* 用户问题 */}
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[10px] text-blue-600">我</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium text-gray-900">{ticket.question}</p>
                          <StatusBadge status={ticket.status} />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">{ticket.createdAt}</p>
                      </div>
                    </div>
                    {/* 服务中心回复 */}
                    {ticket.reply && (
                      <div className="flex items-start gap-2 mt-3 ml-8">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                          <CheckCircle2 className="w-3 h-3 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 leading-relaxed">{ticket.reply}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{ticket.repliedAt}</p>
                        </div>
                      </div>
                    )}
                    {ticket.status === 'processing' && !ticket.reply && (
                      <div className="flex items-center gap-2 mt-3 ml-8">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                          <Clock className="w-3 h-3 text-green-600 animate-pulse" />
                        </div>
                        <p className="text-[10px] text-gray-400">服务中心正在处理中...</p>
                      </div>
                    )}
                    {ticket.status === 'pending' && (
                      <div className="flex items-center gap-2 mt-3 ml-8">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                          <AlertCircle className="w-3 h-3 text-gray-400" />
                        </div>
                        <p className="text-[10px] text-gray-400">已提交，等待服务中心处理</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ScrollArea>

    {/* 提交问题对话框 */}
    <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Headphones className="w-4 h-4 text-gcb-primary" />
            一键服务 — 提交问题
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Textarea
            placeholder="请描述您遇到的问题或需要帮助的内容..."
            value={serviceQuestion}
            onChange={(e) => setServiceQuestion(e.target.value)}
            rows={4}
            className="resize-none"
          />
          <p className="text-xs text-gray-400">提交后服务中心将尽快回复，请耐心等待。</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setShowServiceDialog(false); setServiceQuestion('') }}>
            取消
          </Button>
          <Button
            onClick={handleSubmitTicket}
            disabled={!serviceQuestion.trim()}
            className="bg-gcb-primary hover:bg-gcb-primaryDark text-white"
          >
            <Send className="w-3.5 h-3.5 mr-1" />
            一键提交
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}

/* ---- 状态标签 ---- */
function StatusBadge({ status }: { status: ServiceTicket['status'] }) {
  const config = {
    pending: { label: '待处理', className: 'bg-gray-100 text-gray-500 border-gray-200' },
    processing: { label: '处理中', className: 'bg-blue-50 text-blue-600 border-blue-200' },
    resolved: { label: '已回复', className: 'bg-green-50 text-green-600 border-green-200' },
  }
  const c = config[status]
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${c.className}`}>{c.label}</span>
}

/* ---- 排名卡片组件 ---- */
function RankingCard({
  title,
  icon,
  iconColor,
  iconBg,
  ranking,
}: {
  title: string
  icon: React.ReactNode
  iconColor: string
  iconBg: string
  ranking: {
    totalPeers: number
    loginDaysRank: number
    loginDaysPercentile: number
    reportsCreatedRank: number
    reportsCreatedPercentile: number
    reportsViewedRank: number
    reportsViewedPercentile: number
    activityLevel: string
  }
}) {
  return (
    <Card>
      <CardHeader className="pb-3 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={cn('w-7 h-7 rounded-md flex items-center justify-center', iconBg)}>
              <span className={iconColor}>{icon}</span>
            </div>
            <h3 className="text-xs font-semibold text-gray-700">{title}</h3>
          </div>
          <Badge variant="success" className="text-[10px] h-5">
            {ranking.activityLevel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {/* 活跃度排名 */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-500">活跃度</span>
            <span className="text-xs font-medium text-gray-700">
              第 {ranking.loginDaysRank} / {ranking.totalPeers} 名
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"
              style={{ width: `${ranking.loginDaysPercentile}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">超过 {ranking.loginDaysPercentile}% 的用户</p>
        </div>
        {/* 报表制作排名 */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-500">报表制作</span>
            <span className="text-xs font-medium text-gray-700">
              第 {ranking.reportsCreatedRank} / {ranking.totalPeers} 名
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
              style={{ width: `${ranking.reportsCreatedPercentile}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">超过 {ranking.reportsCreatedPercentile}% 的用户</p>
        </div>
        {/* 报表查看排名 */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-500">报表查看</span>
            <span className="text-xs font-medium text-gray-700">
              第 {ranking.reportsViewedRank} / {ranking.totalPeers} 名
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full"
              style={{ width: `${ranking.reportsViewedPercentile}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">超过 {ranking.reportsViewedPercentile}% 的用户</p>
        </div>
      </CardContent>
    </Card>
  )
}
